/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 *
 */

/**
 * @module bulb-media-embed/bulbmediaembedtoolbar
 */

import { Plugin, WidgetToolbarRepository, isWidget } from 'ckeditor5';

import { isObject } from 'lodash';
import { getToolBarConfig } from '../../editorcustomconfig.constant';

/**
 * The media embed toolbar plugin. It creates a toolbar for media embeds that shows up when the figure is selected.
 *
 * Instances of toolbar components (e.g. buttons) are created based on the
 * {@link module:bulb-video/BulbVideoToolbar#toolbar `bulbVideo.toolbar` configuration option}.
 *
 * @extends Plugin
 */
export default class BulbMediaEmbedToolbar extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [WidgetToolbarRepository] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbMediaEmbedToolbar' as const;
  }

  /**
   * @inheritDoc
   */
  private getSelectedMediaViewWidget(selection) {
    const viewElement = selection.getSelectedElement();

    if (viewElement && !!viewElement.getCustomProperty('embed') && isWidget(viewElement)) {
      return viewElement;
    }

    return null;
  }

  /**
   * @inheritDoc
   */
  public afterInit(): void {
    const { editor } = this;
    const widgetToolbarRepository = editor.plugins.get(WidgetToolbarRepository);

    widgetToolbarRepository.register('embed', {
      items: this.normalizeDeclarativeConfig(getToolBarConfig('embed', CKEBulbData.readOnly)),
      getRelatedElement: this.getSelectedMediaViewWidget,
    });
  }

  /**
   * Convert the dropdown definitions to their keys registered in the ComponentFactory.
   * The registration precess should be handled by the plugin which handles the UI of a particular feature.
   */
  private normalizeDeclarativeConfig(config) {
    return config.map((item: { name: any; }) => (isObject(item) ? item.name : item));
  }

}
